import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'wouter';
import axios from "axios";

const NavigationContext = createContext();

const fetchClassName = async (classId) => {
    // try {
    //     const response = await axios.get(`/web/ui/myClasses/classDetails/${classId}`);
    //
    //     if (response && response.data && response.data.class.className) {
    //         return response.data.class.className;
    //     } else {
    //         console.warn(`Class details fetched for ID ${classId}, but data format is unexpected.`, response.data);
    //         return "Class";
    //     }
    // } catch (error) {
    //     console.error(`Failed to fetch class name for ID ${classId}:`, error);
    //     return "Class";
    // }
    return('Class')
};

const breadcrumbMap = {
    "/web/ui/view/reports": { label: "Reports Home" },
    "/web/ui/view/reports/activityReport": {
        label: "Activity Report",
        getNavigationPath: (currentPath) => {
            const match = currentPath.match(/\/activityReport\/([^\/\?]+)/);
            const basePath = match ? `/web/ui/view/reports/activityReport/${match[1]}` : '/web/ui/view/reports/activityReport';

            // Preserve query params from the current window location
            const queryParams = window.location.search;
            if (queryParams) {
                return `${basePath}${queryParams}`;
            }
            return basePath;
        }
    },
    "/web/ui/view/reports/activityReport/:reportId/student": { label: "Student Report" },
    "/web/ui/view/reports/activityReport/:classId/activitySummary": { label: "Activity Summary" },
    "/web/ui/view/reports/studentReport": { label: "Student Report Search" },
    "/web/ui/view/reports/studentReport/:studentId": { label: "Student Details" },
    "/web/ui/view/reports/activitySummaryManager": { label: "Activity Summary Manager" },
    "/web/ui/view/reports/activitySummaryManager/:classId/activitySummary": { label: "Activity Summary" },
    "/web/ui/view/classes/schoolClasses": { label: "Classes" },
    "/web/ui/view/classes/schoolClasses/class/:classId": {
        label: "Class Details",
        getDynamicLabel: async (params) => fetchClassName(params.classId)
    },
    "/web/ui/view/classes/schoolClasses/class/:classId/classDetails": { label: "Class Students" },
    "/web/ui/view/classes/schoolClasses/bulkClassUpdate": { label: "Bulk Class Update" },
    "/web/ui/view/lessonStudio": { label: "Lesson Studio" },
    "/web/ui/view/lessonStudio/lessonSchedules": { label: "Lesson Schedules" },
    "/web/ui/view/lessonStudio/lessonSchedules/schedule/:scheduleId": { label: "Schedule" },
    "/web/ui/view/lessonStudio/lessonPlans": { label: "Lesson Plans" },
    "/web/ui/view/lessonStudio/lessonPlans/new": { label: "New Lesson Plan" },
    "/web/ui/view/lessonStudio/lessonPlans/plan/:planId": { label: "Plan" },
    "/web/ui/view/lessonStudio/createLessonPlan": { label: "Create Lesson Plan" },
    "/web/ui/view/lessonStudio/plans/:planId": { label: "View Lesson Plan" },
    "/web/ui/view/teacherAssessments/": { label: "Assessments" },
    "/web/ui/view/teacherAssessments/assessmentEditor": { label: "Assessment Editor" },
    "/web/ui/view/teacherAssessments/createAssessment": { label: "Create Assessment" }
};

function normalizePath(path) {
    if (path !== '/' && path.endsWith('/')) {
        path = path.slice(0, -1);
    }
    return path;
}

function pathToSegments(path) {
    return normalizePath(path).split('/').filter(Boolean);
}

function extractParams(route, path) {
    const routeSegments = pathToSegments(route);
    const pathSegments = pathToSegments(path);
    const params = {};
    for (let i = 0; i < routeSegments.length; i++) {
        if (routeSegments[i].startsWith(':')) {
            const paramName = routeSegments[i].substring(1);
            params[paramName] = pathSegments[i];
        }
    }
    return params;
}

const NavigationProvider = ({ children }) => {
    const [isNavVisible, setIsNavVisible] = useState(false);
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [isNavShowingRight, setIsNavShowingRight] = useState(true);
    const [isReactDialogOpen, setIsReactDialogOpen] = useState(false);
    const [drawerComponentName, setDrwrComponent] = useState(null);
    const [drawerLocation, setDrwrLocation] = useState('left');

    const setDrawerComponentName = (name) => setDrwrComponent(name);
    const setDrawerLocation = (location) => setDrwrLocation(location);
    const setNavVisible = (bool) => setIsNavVisible(bool);
    const setDrawerVisible = (bool) => setIsDrawerVisible(bool);
    const setReactDialogOpen = (bool) => setIsReactDialogOpen(bool);

    useEffect(() => {
        if (!isNavVisible) {
            setIsNavShowingRight(false);
        }
    }, [isNavVisible]);

    useEffect(() => {
        if(isDrawerVisible === false){
            setDrawerLocation('left');
        }
    }, [isDrawerVisible]);

    // Breadcrumb stuff
    const [location] = useLocation();
    const [trail, setTrail] = useState([]);
    const [dynamicCrumbLabels, setDynamicCrumbLabels] = useState({});

    const registerDynamicCrumb = useCallback((path, label) => {
        setDynamicCrumbLabels(prev => ({ ...prev, [normalizePath(path)]: label }));
    }, []);

    const unregisterDynamicCrumb = useCallback((path) => {
        setDynamicCrumbLabels(prev => {
            const newLabels = { ...prev };
            delete newLabels[normalizePath(path)];
            return newLabels;
        });
    }, []);

    useEffect(() => {
        const buildBreadcrumbs = async () => {
            const pathSegments = pathToSegments(location);
            const preliminaryTrail = [];

            for (let i = 1; i <= pathSegments.length; i++) {
                const currentSubPath = '/' + pathSegments.slice(0, i).join('/');
                const matchedEntry = Object.entries(breadcrumbMap).find(([route]) => {
                    const routeSegments = pathToSegments(route);
                    if (routeSegments.length !== i) return false;
                    for (let j = 0; j < routeSegments.length; j++) {
                        if (routeSegments[j].startsWith(':')) continue;
                        if (routeSegments[j] !== pathSegments[j]) return false;
                    }
                    return true;
                });

                if (matchedEntry) {
                    const [route, meta] = matchedEntry;
                    preliminaryTrail.push({
                        meta,
                        path: currentSubPath,
                        routePattern: route,
                        label: meta.label,
                    });
                }
            }

            const promises = preliminaryTrail.map(crumb => {
                const normalizedPath = normalizePath(crumb.path);
                if (dynamicCrumbLabels[normalizedPath]) {
                    return Promise.resolve(dynamicCrumbLabels[normalizedPath]);
                }
                if (crumb.meta.getDynamicLabel) {
                    const params = extractParams(crumb.routePattern, crumb.path);
                    return crumb.meta.getDynamicLabel(params);
                }
                return Promise.resolve(crumb.label);
            });

            try {
                const resolvedLabels = await Promise.all(promises);
                const finalTrail = preliminaryTrail.map((crumb, index) => ({
                    path: crumb.meta.getNavigationPath ? crumb.meta.getNavigationPath(location) : crumb.path,
                    label: resolvedLabels[index],
                }));

                // Always prepend Dashboard breadcrumb at the front
                const trailWithDashboard = [
                    { path: '/web/ui/view/dashboard', label: 'Dashboard' },
                    ...finalTrail
                ];

                setTrail(trailWithDashboard);
            } catch (error) {
                console.error("Failed to resolve breadcrumb labels:", error);
                const fallbackTrail = preliminaryTrail.map(crumb => ({ path: crumb.path, label: crumb.label }));

                // Always prepend Dashboard breadcrumb at the front
                const trailWithDashboard = [
                    { path: '/web/ui/view/dashboard', label: 'Dashboard' },
                    ...fallbackTrail
                ];

                setTrail(trailWithDashboard);
            }
        };

        buildBreadcrumbs();
    }, [location, dynamicCrumbLabels]);

    const contextValue = useMemo(() => ({
        isNavVisible,
        isDrawerVisible,
        drawerComponentName,
        drawerLocation,
        setNavVisible,
        setDrawerVisible,
        setDrawerComponentName,
        setDrawerLocation,
        isNavShowingRight,
        setIsNavShowingRight,
        trail,
        registerDynamicCrumb,
        unregisterDynamicCrumb,
        setReactDialogOpen
    }), [
        isNavVisible,
        isDrawerVisible,
        isNavShowingRight,
        trail,
        registerDynamicCrumb,
        unregisterDynamicCrumb
    ]);

    return (
        <NavigationContext.Provider value={contextValue}>
            {children}
        </NavigationContext.Provider>
    );
};

export { NavigationContext, NavigationProvider };